import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { recordVisit } from '../utils/visitorTracker';
import { parseMetaPixelId } from '../utils/metaPixel';

/**
 * Component to handle global tracking:
 * 1. Google Analytics (via Firestore ID)
 * 2. Meta Pixel (via Firestore ID/Code)
 * 3. Internal Firestore Analytics (Visits, Products, Countries)
 */
export default function AnalyticsTracker() {
    const [gaId, setGaId] = useState(null);
    const [metaPixelId, setMetaPixelId] = useState(null);
    const location = useLocation();
    
    // Load config from Firestore
    useEffect(() => {
        async function loadConfig() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "general"));
                if (snap.exists()) {
                    const data = snap.data();
                    if (data.googleAnalyticsId) {
                        setGaId(data.googleAnalyticsId);
                    }
                    if (data.metaPixelId) {
                        const parsedId = parseMetaPixelId(data.metaPixelId);
                        if (parsedId) {
                            setMetaPixelId(parsedId);
                        }
                    }
                }
            } catch (e) {
                console.error("Failed to load tracking configs:", e);
            }
        }
        loadConfig();
    }, []);

    // 1. Google Analytics Initialization
    useEffect(() => {
        if (!gaId) return;
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', gaId);
    }, [gaId]);

    // 2. Meta Pixel Initialization
    useEffect(() => {
        if (!metaPixelId) return;

        // Base Code
        if (!window.fbq) {
            (function (f, b, e, v, n, t, s) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod
                        ? n.callMethod.apply(n, arguments)
                        : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js'
            );
            window.fbq('init', metaPixelId, {
                disableAutomaticEvents: true
            });
        }

        // Inject noscript image fallback
        let noscript = document.getElementById("meta-pixel-noscript");
        if (!noscript) {
            noscript = document.createElement("noscript");
            noscript.id = "meta-pixel-noscript";
            noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1" />`;
            document.body.appendChild(noscript);
        }
    }, [metaPixelId]);

    // 3. Dynamic Page View Tracking
    useEffect(() => {
        // Extract product ID if we are on a book or bundle page
        let productId = null;
        if (location.pathname.startsWith('/book/')) {
            productId = location.pathname.split('/book/')[1];
        } else if (location.pathname.startsWith('/bundle/')) {
            productId = location.pathname.split('/bundle/')[1];
        }

        recordVisit(location.pathname, productId);

        // Google Analytics Pageview
        if (window.gtag && gaId) {
            window.gtag('config', gaId, {
                'page_path': location.pathname
            });
        }

        // Meta Pixel Pageview
        if (window.fbq && metaPixelId) {
            window.fbq('track', 'PageView');
        }
    }, [location.pathname, gaId, metaPixelId]);

    return null;
}
